import React from 'react';
import { alpha, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  ArrowForward,
  Cancel,
  CheckCircle,
  Comment,
  PlayArrow,
} from '@mui/icons-material';

const events = [
  {
    id: 1,
    type: 'requested',
    title: 'State change requested',
    description: 'Review started for Blueberry Variant V3.',
    user: 'Alex Carter',
    date: '06/05/2026 09:41',
    from: 'idea',
    to: 'prototype',
  },
  {
    id: 2,
    type: 'approved',
    title: 'Reviewed by Sophia Smith',
    description: 'Approved for scale-up evaluation.',
    user: 'Sophia Smith',
    date: '06/05/2026 10:02',
    from: 'prototype',
    to: 'scale-up',
  },
  {
    id: 3,
    type: 'comment',
    title: 'Comment added',
    description: 'Please adjust protein content before production review.',
    user: '',
    date: '06/05/2026 11:15',
    from: 'scale-up',
    to: 'production',
  },
  {
    id: 4,
    type: 'rejected',
    title: 'Reviewed by John Snow',
    description: 'Change rejected. Reformulation required.',
    user: 'John Snow',
    date: '06/05/2026 14:32',
    from: 'scale-up',
    to: 'production',
  },
];

const stageColors = {
  idea: '#006BFF',
  prototype: '#148CFF',
  'scale-up': '#FF2D2D',
  production: '#43C936',
};

const eventConfig = {
  requested: {
    icon: <PlayArrow />,
    color: '#148CFF',
  },
  approved: {
    icon: <CheckCircle />,
    color: '#43C936',
  },
  comment: {
    icon: <Comment />,
    color: '#FF4D3D',
  },
  rejected: {
    icon: <Cancel />,
    color: '#FF4D3D',
  },
};

function StageChip({ label }) {
  const color = stageColors[label] || '#006BFF';

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        height: 24,
        fontSize: 11,
        fontWeight: 700,
        color,
        borderColor: color,
        bgcolor: alpha(color, 0.04),
      }}
    />
  );
}

function ReviewHistoryVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 300,
        p: 3,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          p: 2.5,
          bgcolor: 'background.paper',
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Review history
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              Timeline of review events and stage-change decisions.
            </Typography>
          </Box>

          <Chip
            label="4 events"
            size="small"
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        <Stack spacing={0}>
          {events.map((event, index) => {
            const config = eventConfig[event.type];

            return (
              <Box
                key={event.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '34px 1fr auto',
                  columnGap: 1.5,
                  position: 'relative',
                  pb: index < events.length - 1 ? 2.25 : 0,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {index < events.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 34,
                        bottom: -2,
                        width: '1px',
                        bgcolor: 'divider',
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: alpha(config.color, 0.12),
                      color: config.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    {React.cloneElement(config.icon, {
                      sx: { fontSize: 18 },
                    })}
                  </Box>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 800,
                      color: 'text.primary',
                      lineHeight: 1.25,
                    }}
                  >
                    {event.title}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      lineHeight: 1.35,
                      mt: 0.25,
                    }}
                  >
                    {event.description}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      lineHeight: 1.35,
                      mt: 0.35,
                      fontSize: 10.5,
                    }}
                  >
                    {event.user} · {event.date}
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    alignSelf: 'center',
                    pl: 1,
                  }}
                >
                  <StageChip label={event.from} />

                  <ArrowForward
                    sx={{
                      fontSize: 16,
                      color: 'text.secondary',
                    }}
                  />

                  <StageChip label={event.to} />
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
}

export default ReviewHistoryVisual;